import { useEffect, useState } from "react";
import axios from "axios";
import { Instagram, Youtube, MapPin, Crown, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FounderCard() {
  const [founder, setFounder] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/founder`)
      .then((r) => {
        if (r.data?.available) setFounder(r.data);
      })
      .catch(() => {});
  }, []);

  if (!founder) return null;

  const ig = founder.social_instagram?.replace(/^@/, "");
  const tt = founder.social_tiktok?.replace(/^@/, "");
  const yt = founder.social_youtube;

  return (
    <section
      id="founder"
      data-testid="founder-card"
      className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden"
    >
      {/* Backdrop accent */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#4C1D95] rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#DC2626] rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
          Meet the Founder
        </div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] mb-10">
          The Mind Behind <span className="text-[#D4AF37]">OMNIMUSCLE</span>.
        </h2>

        <div className="bg-[#0F0F0F] border-2 border-[#D4AF37] p-6 md:p-10 grid md:grid-cols-[180px,1fr] gap-6 md:gap-10 items-start">
          {/* Avatar / monogram */}
          <div className="relative w-32 h-32 md:w-44 md:h-44 mx-auto md:mx-0">
            {founder.picture ? (
              <img
                src={founder.picture}
                alt={founder.name}
                className="w-full h-full object-contain bg-black border-2 border-[#D4AF37]"
              />
            ) : (
              <div className="w-full h-full bg-[#4C1D95] border-2 border-[#D4AF37] flex items-center justify-center font-display text-7xl text-[#D4AF37]">
                {(founder.name || "R").charAt(0).toUpperCase()}
              </div>
            )}
            <span
              className="absolute -top-3 -right-3 bg-[#D4AF37] text-black w-9 h-9 flex items-center justify-center rounded-full border-2 border-black shadow-lg"
              title="Founder"
            >
              <Crown size={16} strokeWidth={2.5} />
            </span>
          </div>

          {/* Bio block */}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3
                data-testid="founder-name"
                className="font-display text-3xl md:text-4xl text-white leading-tight"
              >
                {founder.full_name || founder.name}
              </h3>
              <span className="bg-gradient-to-r from-[#D4AF37] to-[#f5d96b] text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1">
                <Sparkles size={10} /> Founder
              </span>
            </div>

            {founder.location && (
              <p className="text-xs text-neutral-400 mt-1 inline-flex items-center gap-1.5">
                <MapPin size={12} className="text-[#D4AF37]" /> {founder.location}
              </p>
            )}

            {founder.bio && (
              <p className="text-base md:text-lg text-neutral-200 leading-relaxed mt-4 italic">
                "{founder.bio}"
              </p>
            )}

            {/* Socials */}
            {(ig || tt || yt) && (
              <div className="flex flex-wrap items-center gap-3 mt-6">
                {ig && (
                  <a
                    data-testid="founder-ig"
                    href={`https://instagram.com/${ig}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-omni btn-outline-gold text-xs"
                  >
                    <Instagram size={14} /> @{ig}
                  </a>
                )}
                {tt && (
                  <a
                    data-testid="founder-tt"
                    href={`https://tiktok.com/@${tt}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-omni btn-outline-gold text-xs"
                  >
                    <span className="font-bold">TT</span> @{tt}
                  </a>
                )}
                {yt && (
                  <a
                    data-testid="founder-yt"
                    href={yt.startsWith("http") ? yt : `https://youtube.com/${yt.startsWith("@") ? yt : "@" + yt}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-omni btn-outline-gold text-xs"
                  >
                    <Youtube size={14} /> {yt}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
